'use client';

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
  type FocusEvent,
} from 'react';
import { Popover, PopoverTrigger, PopoverPortal, PopoverContent } from '@radix-ui/react-popover';

export interface HelpTipProps {
  children: ReactNode;
  /** Accessible label for the trigger. Default 'Help'. */
  label?: string;
  /** Persists “seen” in localStorage; the trigger pulses until first opened. */
  storageKey?: string;
  side?: 'top' | 'bottom';
  align?: 'start' | 'end';
  className?: string;
  /** Glyph inside the trigger circle. Default '?'; PageHeader uses 'i'. */
  trigger?: ReactNode;
  /** Hover/focus previews; click pins. Escape/outside press dismiss either state. */
  hoverToOpen?: boolean;
}

function readSeen(key?: string): boolean {
  if (!key || typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(`helptip:${key}`) === '1';
  } catch {
    return true;
  }
}

/** Opt-in help with keyboard/touch dismissal and viewport-constrained content. */
export function HelpTip({
  children,
  label = 'Help',
  storageKey,
  side = 'bottom',
  align = 'start',
  className,
  trigger = '?',
  hoverToOpen = false,
}: HelpTipProps) {
  const [pinned, setPinned] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const [seen, setSeen] = useState(() => readSeen(storageKey));
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const suppressFocusOpenRef = useRef(false);
  const suppressHoverOpenRef = useRef(false);
  const open = pinned || hovering || focused;
  const pulsing = Boolean(storageKey) && !seen;

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target) && !popRef.current?.contains(target)) {
        setPinned(false);
        setHovering(false);
        setFocused(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      suppressHoverOpenRef.current = true;
      setPinned(false);
      setHovering(false);
      setFocused(false);
      // Restoring focus must not reopen a just-dismissed preview. Scope the
      // suppression to this synchronous focus call, never the next real Tab.
      suppressFocusOpenRef.current = true;
      triggerRef.current?.focus();
      suppressFocusOpenRef.current = false;
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    // Hover/focus previews never steal focus. Only a click moves into content.
    if (pinned) popRef.current?.focus();
  }, [pinned]);

  function markSeen() {
    if (!storageKey || seen) return;
    setSeen(true);
    try {
      window.localStorage.setItem(`helptip:${storageKey}`, '1');
    } catch {
      /* storage is optional */
    }
  }

  function toggle() {
    if (pinned) {
      setPinned(false);
      setHovering(false);
      setFocused(false);
    } else {
      markSeen();
      setPinned(true);
    }
  }

  function containsTarget(target: EventTarget | null) {
    return (
      target instanceof Node &&
      (containerRef.current?.contains(target) || popRef.current?.contains(target))
    );
  }

  const previewHandlers = hoverToOpen
    ? {
        onMouseEnter: () => {
          if (suppressHoverOpenRef.current) return;
          markSeen();
          setHovering(true);
        },
        onMouseLeave: (event: ReactMouseEvent) => {
          if (!containsTarget(event.relatedTarget)) {
            setHovering(false);
            if (containerRef.current?.contains(event.target as Node))
              suppressHoverOpenRef.current = false;
          }
        },
        onFocus: () => {
          if (suppressFocusOpenRef.current) return;
          markSeen();
          setFocused(true);
        },
        onBlur: (event: FocusEvent) => {
          if (!containsTarget(event.relatedTarget)) setFocused(false);
        },
      }
    : {};

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setPinned(false);
          setHovering(false);
          setFocused(false);
        }
      }}
    >
      <span
        ref={containerRef}
        className={['relative inline-flex shrink-0', className].filter(Boolean).join(' ')}
        {...previewHandlers}
      >
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={(event) => {
              // A click pins an existing hover preview instead of toggling it closed.
              event.preventDefault();
              toggle();
            }}
            className={[
              'relative inline-flex h-4 w-4 items-center justify-center rounded-full border text-caption focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-11 after:-translate-x-1/2 after:-translate-y-1/2',
              pulsing
                ? 'animate-pulse motion-reduce:animate-none border-brand-primary text-brand-primary'
                : 'border-border-strong text-muted hover:border-primary hover:text-primary',
            ].join(' ')}
          >
            {trigger}
          </button>
        </PopoverTrigger>
        {open && (
          <PopoverPortal
            container={
              containerRef.current?.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ??
              undefined
            }
          >
            <PopoverContent
              ref={popRef}
              role="dialog"
              data-anchored-help=""
              aria-label={label}
              tabIndex={-1}
              {...previewHandlers}
              side={side}
              align={align}
              sideOffset={0}
              collisionPadding={8}
              onEscapeKeyDown={(event) => {
                event.preventDefault();
                suppressHoverOpenRef.current = true;
                setPinned(false);
                setHovering(false);
                setFocused(false);
                suppressFocusOpenRef.current = true;
                triggerRef.current?.focus();
                suppressFocusOpenRef.current = false;
              }}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              style={{ pointerEvents: 'auto', boxShadow: 'var(--shadow-lg)' }}
              className="z-popover w-64 max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)] overflow-y-auto break-words rounded-control border border-border bg-surface-elevated p-3 text-body-sm text-secondary outline-none"
            >
              {children}
            </PopoverContent>
          </PopoverPortal>
        )}
      </span>
    </Popover>
  );
}
